<template>
    <!-- Modal de Movimentação de Equipamento -->
    <v-dialog
        v-model="visible"
        max-width="600px"
        persistent
    >
        <v-card>

            <v-toolbar color="primary" dark>

                <div 
                    class="d-flex align-center white--text" 
                    style="white-space: nowrap; overflow: visible; margin-left: 10px; font-size: 20px;"
                >
                    <v-icon class="mr-2">mdi-truck</v-icon>
                    Registro Avaria Equipamento: {{ dadosOrigem.equipamento }}
                </div>
                
                <v-spacer />
                
                <v-btn icon @click="fecharModal">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pa-6">

                <v-form ref="formRef" v-model="formValido">
                
                    <!-- Seleção Avaria -->
                    <v-select
                        v-model="avariaSelecionada"
                        :items="listaAvarias"
                        item-title="nome"
                        item-value="id"
                        label="Selecione Tipo de Avaria"
                        :loading="carregandoListaAvarias"
                        :rules="[v => !!v || 'Selecione Avaria do Equipamento']"
                        clearable
                        class="mb-4"
                        return-object
                    />

                    <!-- Observação Avaria -->
                    <v-textarea
                        v-model="observacaoAvaria"
                        label="Descreva Motivo da Avaria Do Equipamento"
                        :rules="[
                            v => !!v || 'Informe o motivo da avaria',
                            v => (v && v.length >= 10) || 'Mínimo 10 caracteres',
                        ]"
                        rows="3"
                        auto-grow
                        counter="500"
                        placeholder="Descreva Motivo da Avaria Do Equipamento..."
                        class="mb-4"
                    />
                    
                    <v-switch
                        v-model="enviarManutecao"
                        :label="enviarManutecao ? 'Enviar Manutenção' : 'Não Enviar Manutenção'"
                        :color="enviarManutecao ? 'blue' : 'red'"                                
                    />

                </v-form>
            
            </v-card-text>

            <v-card-actions class="pa-4 pt-0">
                
                <v-spacer />
                
                <v-btn
                    variant="outlined"
                    @click="fecharModal"
                    class="mr-2"
                >
                    Cancelar
                </v-btn>
                
                <v-btn
                    color="primary"
                    :loading="carregandoListaAvarias"
                    :disabled="!formValido || carregandoListaAvarias"
                    @click="confirmarAvaria"
                >
                    <v-icon start>mdi-check</v-icon>
                    Registrar Avaria
                </v-btn>

            </v-card-actions>

        </v-card>

    </v-dialog>

</template>

<script setup>

    // Importa referências do vue
    import { ref, reactive, watch, defineEmits } from 'vue'
    
    // IMporta biblioteca axios http
    import axios from 'axios'

    // Importa store de login
    import { useLoginStore } from '@/stores/loginStore'

    // Importa store de erro
    import { useErroStore } from '@/stores/erroStore'

    // Importa o componente do Snackbar
    import { useSnackbarStore } from '@/stores/snackbar'

    // Monta endereço da api
    const API_BASE_URL = import.meta.env.VITE_API_URL           
            
    // Define a emissão de comando para o formulário que chamou indicando que fechou o modal
    const emit = defineEmits(['registro-avaria-concluido', 'fechar'])

    // Importa a store de erros
    const erroStore = useErroStore()

    // Importa a store de chamados
    const loginStore = useLoginStore()

    // Importa a store do snackbar
    const snackbar = useSnackbarStore()
    
    // Variáveis de referência
    const formRef = ref(null)
    const formValido = ref(false)
    const carregandoListaAvarias = ref(false)
    
    // Estado do modal
    const visible = ref(false)

    // Dados da origem (preenchidos quando abre o modal)
    const dadosOrigem = reactive({
        idKit: null,
        idEquipamentoKit: null,
        idEquipamento: null,
        equipamento: null        
    })
    
    const listaAvarias = ref([])
    const avariaSelecionada = ref(null)
    const observacaoAvaria = ref(null)
    const enviarManutecao = ref(true)

    // Limpa dados do modal
    const resetarEstado = () => {

        // Limpa lista de setores
        listaAvarias.value = []
        
        avariaSelecionada.value = null
        observacaoAvaria.value = ''
        
        // Reseta o formulário
        if (formRef.value) {
            formRef.value.reset()
        }
    }

    // Metodo para abrir formulário modal
    const abrirModal = async (dados) => {

        try {
            
            // Limpa dados do formulário modal
            resetarEstado()

            // Salva os dados recebidos no array dadosOrigem
            dadosOrigem.idKit = dados.idKit,
            dadosOrigem.idEquipamentoKit = dados.idEquipamentoKit,
            dadosOrigem.idEquipamento = dados.idEquipamento,
            dadosOrigem.equipamento = dados.equipamento   
            
            // Carrega lista de empresas
            await carregaAvarias()            
            
            // Abre o modal
            visible.value = true

        } catch (erro) {    
            erroStore.exibirErro(erro)('Erro Ao Preparar Registro Avarias: ' + erro)
        }
    }

    // Metodo para que fecha o modal
    const fecharModal = () => {

        // Limpa dados do formulário modal
        resetarEstado()

        // Torna modal invisível
        visible.value = false
    }

    // Carrega lista de empresas
    const carregaAvarias = async () => {

        try {

            carregandoListaAvarias.value = true

            // Carrega lista de empresas
            const { data } = await axios.get(`${API_BASE_URL}/administracao/carregaListaAvarias`, {
                params: {
                    ordenacaoPor: 'nome',
                    direcaoOrdenacao: 'asc'
                },
                headers: {
                    'Authorization': `Bearer ${loginStore.token}`
                }
            })

            // Filtra para não incluir a empresa de origem
            listaAvarias.value = data.data
                .filter(avaria => avaria.nome !== "NENHUMA")
                .map(avaria => ({
                    nome: avaria.nome,
                    id: avaria.idAvaria
                })
            )
            
        } catch (erro) {
            
            carregandoListaAvarias.value = false
            erroStore.exibirErro('Erro Ao Carregar Lista Avarias: ' + erro)
            listaEmpresas.value = []

        } finally {
            carregandoListaAvarias.value = false
        }
    }

    // Rotina chamada pelo botão de confirmçao de movimento
    const confirmarAvaria = async () => {

        // Valida formulário
        if (!formRef.value) return
    
        // Valida dados do formulário de envio
        const { valid } = await formRef.value.validate()

        // Se não forem válidos sai da rotinr
        if (!valid) return

        try {
            
            // Liga indicação que está carregandoAvaria equipamento
            carregandoListaAvarias.value = true

            // Monta array com os dados de envio
            const dadosAvaria = {
                idAvaria: avariaSelecionada.value.id,
                observacaoAvaria: observacaoAvaria.value.trim(),                
                idKit: dadosOrigem.idKit,
                idEquipamentoKit: dadosOrigem.idEquipamentoKit,
                idEquipamento: dadosOrigem.idEquipamento,
                equipamento: dadosOrigem.equipamento,
                enviarManutecao: enviarManutecao
            }

            // Emite evento de sucesso
            emit('registro-avaria-concluido', dadosAvaria)

            // Fecha formulário modal
            fecharModal()
        
        } catch (erro) {
            erroStore.exibirErro('Erro ao Registrar Avaria Equipamento: ' + erro)
        } finally {
            carregandoListaAvarias.value = false
        }
    }

    // Watch para limpar setor quando empresa mudar
    watch(() => avariaSelecionada, (novaAvaria) => {
    
        if (!novaAvaria) {
            avariaSelecionada.value = null
        }
    })

    // Expor métodos para o componente pai
    defineExpose({
        abrirModal,
        fecharModal
    })

</script>

<style scoped>

    .v-card-text {
        padding-top: 20px;
    }

    .v-textarea textarea {
        min-height: 80px;
    }

</style>